import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { personUdvalgForm } from "../../../../../config/form/person/personUdvalgFormConfig";
import { defaultPersonUdvalgColumns } from "../../../../../config/person";
import { useGenericQuery } from "../../../../../hooks/useQueries";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import { PersonDto, PersonUdvalg } from "../../../../../interfaces/schema";
import { PersonUdvalgField } from "../../../../../interfaces/personer";
import { IColumn, SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import { periodeIsActive } from "../../../../../util/date";
import { orderBy } from "../../../../../util/orderBy";

export default function Udvalgsposter({
  columns,
  invalidateData,
  personId,
  personUdvalg,
  setActiveColumns,
}: {
  personId: string;
  invalidateData: InvalidateDataFunction<any>;
  personUdvalg: PersonUdvalg[];
  columns: IColumn<PersonUdvalgField>[];
  setActiveColumns: React.Dispatch<React.SetStateAction<PersonUdvalgField[]>>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "BasisWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personUdvalgsposterSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );
  const queryValues = useGenericQuery<IUdvalg>("udvalg", {
    orderBy: "navn",
  });

  const { data } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id: personId },
  });
  // event handlers
  function downloadHandler() {
    tableToXLSX(columns, personUdvalg, {
      fileName: `udvalgsposter-for-${data?.person.kundeNr}`,
      additionalData: {
        kundenr: data?.person.kundeNr,
      },
    });
  }

  if (!personUdvalg.length) {
    return (
      <IngenRegistreringer
        editConfig={
          canWrite
            ? personUdvalgForm(queryValues, personId, undefined)
            : undefined
        }
        invalidateData={invalidateData}
      />
    );
  }

  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <ModalProvider>
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <>
              <th className="indicator"></th>
              {getHeaderActiveColumns(columns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <TableBody>
            {orderBy(
              personUdvalg,
              sortBy.map((ds) => ds.field),
              sortBy.map((ds) => ds.direction),
            ).map((pu) => {
              return (
                <TableRow
                  key={pu.id}
                  editConfig={
                    canWrite
                      ? personUdvalgForm(queryValues, personId, pu)
                      : undefined
                  }
                  invalidateData={invalidateData}
                >
                  <td
                    className={`indicator ${
                      periodeIsActive(
                        new Date(pu.periode.fraDato),
                        new Date(pu.periode.tilDatoInklusiv),
                      )
                        ? " has-effect"
                        : ""
                    }  `}
                  >
                    <ActiveIcon />
                  </td>
                  <>
                    {columns
                      .filter((col) => col.active)
                      .map((col) => {
                        return <TD key={col.field} col={col} rowData={pu} />;
                      })}
                  </>
                </TableRow>
              );
            })}
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          defaultColumns={defaultPersonUdvalgColumns}
          columns={columns}
          setActiveColumns={setActiveColumns}
        />
        <OpretNyButtonBlock
          editConfig={
            canWrite ? personUdvalgForm(queryValues, personId) : undefined
          }
          invalidateData={invalidateData}
        />
      </ModalProvider>
    </>
  );
}
const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];
